/**
 * Routes (§12.3).
 *
 * Phase 1 lands the tunnels surface and onboarding. The routes that remain placeholders —
 * inspector, domains, traffic, logs, access, billing — are still declared so the sidebar's nav
 * is real, and each names the phase it arrives in rather than 404ing.
 *
 * `/docs` is the only unguarded branch. Everything else carries `meta.auth`, and the guard below
 * sends an anonymous visitor to `/login` with a redirect back — which is the right behaviour
 * everywhere except on a page explaining how to get an account.
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useWorkspaceStore } from '@/stores/workspace'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/pages/auth/LoginPage.vue'),
    meta: { guest: true, title: 'Sign in' },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/pages/auth/RegisterPage.vue'),
    meta: { guest: true, title: 'Create your account' },
  },
  {
    // §12.3: guarded by `auth` only. A user arriving from their terminal mid-onboarding must
    // land here rather than being redirected — the flow they started is the one to finish.
    path: '/device',
    name: 'device-approval',
    component: () => import('@/pages/device/DeviceApprovalPage.vue'),
    meta: { auth: true, title: 'Authorise device' },
  },
  {
    path: '/2fa',
    name: 'two-factor',
    component: () => import('@/pages/auth/TwoFactorPage.vue'),
    meta: { guest: true, title: 'Two-factor authentication' },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/pages/auth/ForgotPasswordPage.vue'),
    meta: { guest: true, title: 'Reset your password' },
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    component: () => import('@/pages/auth/ResetPasswordPage.vue'),
    meta: { guest: true, title: 'Choose a new password' },
  },
  {
    path: '/verify-email',
    name: 'verify-email',
    component: () => import('@/pages/auth/VerifyEmailPage.vue'),
    meta: { auth: true, title: 'Verify your email' },
  },
  {
    path: '/oauth/callback',
    name: 'oauth-callback',
    component: () => import('@/pages/auth/OAuthCallbackPage.vue'),
    meta: { title: 'Signing you in' },
  },

  {
    /**
     * §14.4 renders onboarding "inside the marketing chrome (nav + footer), not the app
     * shell". No marketing site exists and D-007 keeps it out of scope, so the distinction
     * being drawn — no app chrome around a first-run flow — is served by a minimal in-SPA
     * shell. Recorded in DECISIONS.md D-024.
     */
    path: '/onboarding',
    component: () => import('@/components/layout/MarketingShell.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'onboarding',
        component: () => import('@/pages/onboarding/OnboardingPage.vue'),
        meta: { title: 'Get started' },
      },
    ],
  },

  {
    /**
     * The docs (§18 item 12), as routes in this SPA rather than §12.1's separate Nuxt site —
     * D-024 clause 10.
     *
     * A third `MarketingShell` parent, and **deliberately without `meta: { auth: true }`**: the
     * existing one carries it, and installation instructions that bounce an anonymous reader to
     * `/login` are instructions nobody can follow. These are the only public routes here.
     */
    path: '/docs',
    component: () => import('@/components/layout/MarketingShell.vue'),
    children: [
      {
        path: '',
        component: () => import('@/pages/docs/DocsLayout.vue'),
        children: [
          // Landing on the first page rather than on an index that only lists five links.
          { path: '', redirect: '/docs/installation' },
          {
            path: 'installation',
            name: 'docs-installation',
            component: () => import('@/pages/docs/InstallationPage.vue'),
            meta: { title: 'Installation' },
          },
          {
            path: 'quick-start',
            name: 'docs-quick-start',
            component: () => import('@/pages/docs/QuickStartPage.vue'),
            meta: { title: 'Quick Start' },
          },
          {
            path: 'http',
            name: 'docs-http',
            component: () => import('@/pages/docs/HttpCommandPage.vue'),
            meta: { title: 'nlocal http' },
          },
          {
            path: 'login',
            name: 'docs-login',
            component: () => import('@/pages/docs/LoginCommandPage.vue'),
            meta: { title: 'nlocal login' },
          },
          {
            path: 'nlocal-yml',
            name: 'docs-nlocal-yml',
            component: () => import('@/pages/docs/TunnelFilePage.vue'),
            meta: { title: 'nlocal.yml' },
          },
        ],
      },
    ],
  },

  {
    path: '/',
    component: () => import('@/components/layout/AppShell.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'overview',
        component: () => import('@/pages/app/OverviewPage.vue'),
        meta: { title: 'Overview' },
      },
      // Declared so the shell's navigation is real. Each lands with its phase.
      { path: 'tunnels', name: 'tunnels', component: () => import('@/pages/app/TunnelsPage.vue'), meta: { title: 'Tunnels' } },
      {
        // §12.3: the detail tabs are child routes, not local state — a tab is a place, and
        // one someone should be able to link a colleague to.
        path: 'tunnels/:id',
        component: () => import('@/pages/app/TunnelDetailPage.vue'),
        meta: { title: 'Tunnel' },
        children: [
          { path: '', name: 'tunnel', component: () => import('@/pages/app/tunnel/TunnelOverviewTab.vue') },
          { path: 'requests', name: 'tunnel-requests', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Tunnel requests', phase: 'Phase 2' } },
          { path: 'traffic', name: 'tunnel-traffic', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Tunnel traffic', phase: 'Phase 2' } },
          { path: 'logs', name: 'tunnel-logs', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Tunnel logs', phase: 'Phase 2' } },
          { path: 'configuration', name: 'tunnel-configuration', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Tunnel configuration', phase: 'Phase 3' } },
          { path: 'access', name: 'tunnel-access', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Tunnel access', phase: 'Phase 4' } },
        ],
      },
      { path: 'inspector', name: 'inspector', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Request Inspector', phase: 'Phase 2' } },
      { path: 'endpoints', name: 'endpoints', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Endpoints', phase: 'Phase 3' } },
      { path: 'domains', name: 'domains', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Domains & DNS', phase: 'Phase 3' } },
      { path: 'traffic', name: 'traffic', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Traffic & Analytics', phase: 'Phase 2' } },
      { path: 'logs', name: 'logs', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Logs', phase: 'Phase 2' } },
      { path: 'settings/access', name: 'access-policies', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Access Policies', phase: 'Phase 4' } },
      { path: 'settings/tokens', name: 'tokens', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'API Keys & CLI Tokens', phase: 'Phase 1' } },
      { path: 'settings/billing', name: 'billing', component: () => import('@/pages/app/ComingSoonPage.vue'), meta: { title: 'Usage & Billing', phase: 'Phase 5' } },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/pages/app/SettingsPage.vue'),
        meta: { title: 'Settings' },
      },
    ],
  },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/NotFoundPage.vue'),
    meta: { title: 'Not found' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (_to, _from, saved) => saved ?? { top: 0 },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  // One session probe per page load. Guards on every route would otherwise each fire their
  // own /v1/me on the first navigation.
  if (!auth.resolved) {
    await auth.fetchUser()
  }

  /**
   * Seed the workspace store from whatever the auth store knows, on every navigation where it
   * is still empty.
   *
   * Not only after `fetchUser`. Signing in sets the user without touching this store, and
   * `auth.resolved` is already true by then — from the probe on the login page that came back
   * unauthenticated. So the first navigation after a login used to run with no workspace at
   * all, which left `current` null and made any guard that reads workspace state silently
   * fall through. That is how `!workspace.onboarded` (§12.3) failed to send a new user to
   * onboarding: the flag was not missing, the workspace was.
   */
  if (auth.isAuthenticated) {
    const workspaces = useWorkspaceStore()
    if (workspaces.workspaces.length === 0) {
      workspaces.setWorkspaces(auth.user?.workspaces ?? [])
    }
  }

  if (to.meta.auth && !auth.isAuthenticated) {
    // Remember where they were headed so the post-login redirect is not always the overview.
    return { name: 'login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }

  if (to.meta.guest && auth.isAuthenticated) {
    return { name: 'overview' }
  }

  /**
   * §12.3 guards `/onboarding` with `!workspace.onboarded`.
   *
   * Both directions matter. Without the second, the screen is unreachable by the only person
   * it exists for — someone whose workspace has never been set up lands on an Overview full of
   * empty states with no route to the flow that fills them.
   */
  if (auth.isAuthenticated) {
    const workspaces = useWorkspaceStore()
    const onboarded = workspaces.current?.onboarded

    if (to.name === 'onboarding' && onboarded === true) {
      return { name: 'overview' }
    }
    if (to.name === 'overview' && onboarded === false) {
      return { name: 'onboarding' }
    }
  }

  return true
})

router.afterEach((to) => {
  const title = to.meta.title as string | undefined
  document.title = title ? `${title} · nlocal` : 'nlocal'
})
